import React from 'react';
import styled from 'styled-components';
import InputDate from 'components/input-date';
import { format, formatInTimeZone, utcToZonedTime } from 'date-fns-tz';
import { isAutoFilledBlock, zoneLabel } from 'e-signature/helpers';

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  &.mode-view {
    display: flex;
    align-items: center;
    overflow: hidden;
    white-space: nowrap;
  }
  &.locked {
    overflow: hidden;
    white-space: nowrap;
    padding: 0 6px;
    background-color: #f2f2f2;
    border: 1px dashed #b5b5b5;
  }
  .DayPickerInput-OverlayWrapper {
    z-index: 999;
  }
  > div {
    font-size: ${({ fontSize = 14 }) => fontSize}px;
  }
  > div.placeholder {
    color: #707070;
    font-style: italic;
  }
  input {
    height: 100%;
    width: 100%;
    background-color: #fff;
    border: 1px solid #000 !important;
    box-shadow: none;
    border-radius: 0;
    box-sizing: border-box;
    font-size: ${({ fontSize = 14 }) => fontSize}px;
    background-color: #fff175;
  }
`;

const VIEWER_TZ = Intl.DateTimeFormat().resolvedOptions().timeZone;

// Autofilled signing dates are true instants → convert into the document's timezone, so the signer
// and the finished document (which the main app renders against that same zone) land on the same
// day. Picked dates are calendar days (stored at 9AM in the picker's timezone) → re-anchor the UTC
// day instead of converting, which could shift the displayed day.
const formatBlockDate = (block, timezone) => {
  if (!block.value) return '';
  const date = new Date(block.value);
  // Fall back to UTC, not the viewer's zone, so this lands on the same calendar day as the main
  // app's renderer, which is what produces the finished PDF.
  if (isAutoFilledBlock(block)) {
    const tz = timezone || 'UTC';
    return `${formatInTimeZone(date, tz, 'MM/dd/yyyy')} ${zoneLabel(date, tz)}`.trim();
  }
  return format(utcToZonedTime(date, 'UTC'), 'MM/dd/yyyy zzz', { timeZone: VIEWER_TZ });
};

export default ({ block, mode, updateBlock, timezone }) => {
  // The server stamps this block's value when the document is signed and discards whatever the
  // client submits for it. Showing a date picker would let the signer set a date that has no
  // effect on the finished document — so show what will happen instead, in every mode.
  if (isAutoFilledBlock(block)) {
    // The dashed box is a "this will be filled for you" affordance, so it belongs only on a field
    // that is still waiting. Once stamped — which is what every already-signed document shows — it
    // would read as an incomplete field instead of a finished one.
    return block.value ? (
      <Wrapper className={`mode-${mode}`} fontSize={block.fontSize}>
        <div>{formatBlockDate(block, timezone)}</div>
      </Wrapper>
    ) : (
      <Wrapper className={`mode-${mode} locked`} fontSize={block.fontSize}>
        <div className="placeholder">Auto-fills when you sign</div>
      </Wrapper>
    );
  }

  return (
    <Wrapper className={`mode-${mode}`} fontSize={block.fontSize}>
      {mode === 'view' && <div>{block.value && formatBlockDate(block, timezone)}</div>}
      {mode === 'edit' && (
        <InputDate
          value={block.value}
          onChange={(event) => updateBlock({ uuid: block.uuid, value: event.target.value })}
          autoScroll={false}
          placeholder={block.name}
        />
      )}
    </Wrapper>
  );
};
